import { z } from "zod";
/**
 * How long a recording's transcript stays on the server after the recording
 * ended. The server keeps serving it (the `recording-final` grace record) and
 * then deletes the Cortex session — transcript, annotations, debug audio and
 * stored key — when this window is over. Clients show it as a countdown so
 * participants download their copy in time; the copy kept in the browser is
 * not affected.
 */
export const RECORDING_RETENTION_MS = 10 * 60 * 1000;
/** Body of `POST /v1/notifications/stop-recording`; start extends it. */
export const StopRecordingRequestSchema = z.object({
    roomId: z.string().min(1).max(256),
    /**
     * Display name of the user starting the recording. Surfaced to other clients
     * as "Recording started by …" (they otherwise only know the andorUUID). Falls
     * back to the andorUUID server-side when absent — same convention as the join
     * / kick flows.
     */
    userName: z.string().max(256).optional(),
    /**
     * Spoken recording announcements: what the transcription bot says when it
     * joins the room / just before it leaves. Both are project-level Cortex
     * settings captured at session start, so they can only be set via this
     * (start) endpoint — never mid-session or at stop. Omitted fields fall back
     * to the server defaults in `src/cortex.ts`. Limits mirror Cortex's own
     * (non-blank, max 500 chars; a blank text is a Cortex 400).
     */
    joinText: z.string().trim().min(1).max(500).optional(),
    leaveText: z.string().trim().min(1).max(500).optional(),
});
/**
 * A room media key: the exact string an encrypted room's members hand to the
 * ODIN cipher for one key epoch, `HKDF-SHA256(PBKDF2-SHA256(password,
 * "andor/room-media/v1/" + roomId), "andor/room-media/v1/epoch/" + epoch)` as
 * unpadded base64url. It never reveals the password or another epoch's key.
 */
export const RoomMediaKeySchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const StartRecordingRequestSchema = StopRecordingRequestSchema.extend({
    /**
     * Password rooms only (required there): the epoch the recording will run in,
     * which must be the room's current epoch + 1 — starting a recording rotates
     * the room key so the recorder never holds the key of earlier audio — and
     * the media key for that epoch, for the transcription bot. The server never
     * stores or logs the key.
     */
    mediaKeyEpoch: z.number().int().min(1).optional(),
    mediaKey: RoomMediaKeySchema.optional(),
});
/**
 * `POST /v1/notifications/start-recording` 409 body when the request's
 * `mediaKeyEpoch` is not the room's current epoch + 1 (the client missed a
 * rotation). `mediaKeyEpoch` is the current epoch: adopt it and retry.
 */
export const MediaKeyStaleSchema = z.object({
    code: z.literal("MEDIA_KEY_STALE"),
    message: z.string(),
    mediaKeyEpoch: z.number().int().min(0),
});
/**
 * `room-media-key` server event: an encrypted room's current key epoch. Sent
 * to the room's admitted members whenever a recording start or stop rotates
 * the key, and to a member when they first report the room over the socket.
 */
export const RoomMediaKeyEpochEventSchema = z.object({
    roomId: z.string(),
    epoch: z.number().int().min(0),
});
/**
 * One transcript line. `id` is the Cortex message id — the `segmentId` of the
 * bot's live `cortex.transcript` room frames — so clients dedupe on it when
 * merging those frames with the backlog fetch. Only user-speech lines are
 * returned (Cortex `system` join/leave messages are filtered out server-side).
 */
export const TranscriptEntrySchema = z.object({
    id: z.string(),
    timestamp: z.string(),
    senderName: z.string(),
    content: z.string(),
});
/**
 * `POST /v1/notifications/stop-recording` response. The transcript is no longer
 * returned here — clients build the log from the bot's live room frames (+ the
 * backlog endpoint), so stop only confirms success.
 */
export const StopRecordingResponseSchema = z.object({
    success: z.literal(true),
});
/**
 * `GET /v1/notifications/transcript?roomId=…` response — the current session's
 * user-speech transcript so far, and the source of truth for it: a client that
 * joined mid-recording (or reloaded) backfills earlier lines, and every client
 * completes its copy from it once the recording ends, since live room frames
 * may miss lines. `startedAt`/`startedByName` mirror the room's `recording`
 * state so the client can key/label the session; live frames carry neither, so
 * clients bind them to the session through this response.
 */
export const TranscriptBacklogResponseSchema = z.object({
    roomId: z.string(),
    startedAt: z.number(),
    startedByName: z.string(),
    messages: z.array(TranscriptEntrySchema),
});
